import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack
} from '@chakra-ui/react';
import { useUserDecks } from '../../hooks/useDeck';
import { useCardPrint } from '../../views/card-print/useCardPrint';
import { DeckSearchSuggestionList } from '../../components/deck/DeckSearchSuggestionList';
import { RiftboundSwitch } from '../../components/ui/switch';
import { DeckSearchSuggestionRowLoadingTrailing } from '../../components/deck/DeckSearchSuggestionRow';
import { deckMatchesGalleryTextSearch } from '../../utils/deckGallerySearch';
import { RiftboundInput } from '../../components/ui/input';

/** Rough viewport height for ~3 deck rows before scrolling. */
const PREVIEW_LIST_MAX_HEIGHT = 'min(228px, 42vh)';

export function DeckImportSection() {
  const { decks, loading } = useUserDecks();
  const { importFromDeck, importFromDeckLoading } = useCardPrint();
  const [search, setSearch] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);

  const searching = Boolean(search.trim());

  const displayedDecks = useMemo(() => {
    if (searching) {
      return decks.filter((d) => deckMatchesGalleryTextSearch(d, search));
    }
    return decks;
  }, [decks, search, searching]);

  const getRowProps = useCallback(
    (deck: (typeof decks)[number]) => {
      const isLoading = importFromDeckLoading === deck.id;
      return {
        disabled: isLoading,
        onActivate: () => void importFromDeck(deck, { missingOnly }),
        trailing: isLoading ? <DeckSearchSuggestionRowLoadingTrailing /> : undefined,
      };
    },
    [importFromDeck, importFromDeckLoading, missingOnly],
  );

  if (!loading && decks.length === 0) return null;

  return (
    <VStack align="stretch" gap={5} p={4} bg="bgElevated" borderRadius="lg">
      <Box>
        <Heading as="h2" size="sm" mb={2}>
          Proxy My Decks
        </Heading>
        {!loading && decks.length > 1 && (
          <RiftboundInput
            placeholder="Filter decks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            borderColor="border"
          />
        )}
      </Box>

      <HStack justify="space-between" align="center" gap={3}>
        <Text fontSize="sm" fontWeight="semibold" lineHeight="short" minW={0}>
          Add only cards missing from collection
        </Text>
        <RiftboundSwitch
          checked={missingOnly}
          onCheckedChange={(e) => setMissingOnly(e.checked)}
          size="sm"
          flexShrink={0}
        />
      </HStack>

      {loading ? (
        <Flex justify="center" py={4}>
          <Spinner size="sm" color="accent" />
        </Flex>
      ) : (
        <DeckSearchSuggestionList
          decks={displayedDecks}
          emptyMessage={searching ? `No decks match "${search.trim()}"` : undefined}
          maxHeight={searching ? 'min(420px, 62vh)' : PREVIEW_LIST_MAX_HEIGHT}
          hideScrollbar
          getRowProps={getRowProps}
        />
      )}
    </VStack>
  );
}
